import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Ban, ShieldCheck, X, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF, STATUS_LABEL, STATUS_COLOR } from "@/utils/format";
import { toast } from "sonner";

export const CANCEL_REASON_LABEL = {
  erreur_commande: "Erreur dans la commande",
  changement_avis: "Changement d'avis",
  delai_trop_long: "Délai trop long",
  imprevu_livraison: "Imprévu livraison",
  rupture: "Rupture de stock",
  client_injoignable: "Client injoignable",
  adresse_introuvable: "Adresse introuvable",
  autre: "Autre",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const monthsSince = (iso) => {
  if (!iso) return "—";
  const months = Math.floor((Date.now() - new Date(iso).getTime()) / (30.44 * 24 * 3600 * 1000));
  if (months < 1) return "moins d'1 mois";
  if (months < 12) return `${months} mois`;
  const y = Math.floor(months / 12);
  return `${y} an${y > 1 ? "s" : ""}${months % 12 ? ` ${months % 12} mois` : ""}`;
};

function SummaryCard({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-elevated border border-sand p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-mute font-bold">{label}</div>
      <div className="font-display text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
}

/** Fiche détaillée d'un client (admin) : récap, blocage avec motif, historique complet. */
export default function ClientDetailModal({ userId, onClose, onChanged }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [blockMode, setBlockMode] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await apiClient.get(`/admin/clients/${userId}`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  const setBlocked = async (blocked, reason) => {
    setSaving(true);
    try {
      await apiClient.post(`/admin/clients/${userId}/block`, { blocked, reason });
      toast.success(blocked ? t("admin.clientBlocked") : t("admin.clientUnblocked"));
      setBlockMode(false);
      setBlockReason("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const c = data?.client;
  const s = data?.summary;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="client-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto">
        {!data ? (
          <div className="py-16 grid place-items-center text-mute"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                  {c.name}
                  {c.blocked && (
                    <span className="text-[10px] uppercase tracking-widest bg-brand-red text-white px-2 py-0.5 rounded-full" data-testid="client-blocked-badge">{t("admin.blockedBadge")}</span>
                  )}
                </h3>
                <div className="text-sm text-mute">{c.phone || "—"}{c.email ? ` · ${c.email}` : ""}</div>
              </div>
              <button onClick={onClose} data-testid="client-detail-close" className="text-mute hover:text-ink text-sm font-semibold">Fermer ✕</button>
            </div>

            {c.blocked && (
              <div className="mt-2 rounded-xl bg-[#FDECEC] border border-brand-red/30 px-3 py-2 text-sm" data-testid="client-block-info">
                Bloqué le {fmtDate(c.blocked_at)}{c.blocked_by_name ? ` par ${c.blocked_by_name}` : ""} — motif : « {c.blocked_reason} »
              </div>
            )}

            {/* Récapitulatif */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              <SummaryCard label={t("admin.totalSpent")} value={formatGNF(s.total_spent)} testid="client-total-spent" />
              <SummaryCard label={t("admin.totalRefunded")} value={formatGNF(s.total_refunded)} testid="client-total-refunded" />
              <SummaryCard label={t("admin.accountAge")} value={monthsSince(s.account_created_at)} testid="client-account-age" />
              <SummaryCard label={t("admin.lastOrder")} value={fmtDate(s.last_order_at)} testid="client-last-order" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              <SummaryCard label={t("admin.orders")} value={s.total_orders} testid="client-orders-count" />
              <SummaryCard label={t("admin.thDelivered")} value={s.delivered} testid="client-delivered-count" />
              <SummaryCard label={t("admin.cancelledByClient")} value={s.cancelled} testid="client-cancelled-count" />
              <SummaryCard label={t("admin.cancelRate")} value={`${s.cancel_rate}%`} testid="client-cancel-rate" />
            </div>

            {/* Blocage / Déblocage */}
            <div className="mt-4 rounded-xl border border-sand p-3">
              {!c.blocked && !blockMode && (
                <button onClick={() => setBlockMode(true)} data-testid="client-block-btn"
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-3.5 py-2 font-semibold">
                  <Ban className="w-3.5 h-3.5" /> Bloquer ce client
                </button>
              )}
              {!c.blocked && blockMode && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-widest text-mute font-bold">Motif du blocage (obligatoire)</div>
                  <textarea value={blockReason} onChange={(e) => setBlockReason(e.target.value)} rows={2}
                    placeholder={t("admin.blockPh")}
                    data-testid="client-block-reason"
                    className="w-full bg-elevated rounded-xl px-3 py-2 text-sm outline-none border border-sand focus:border-brand-red" />
                  <div className="flex gap-2">
                    <button onClick={() => setBlocked(true, blockReason)} disabled={saving || !blockReason.trim()}
                      data-testid="client-block-confirm"
                      className="inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-red text-white px-3.5 py-2 font-semibold disabled:opacity-40">
                      <Ban className="w-3.5 h-3.5" /> Confirmer le blocage
                    </button>
                    <button onClick={() => { setBlockMode(false); setBlockReason(""); }} data-testid="client-block-cancel"
                      className="text-xs rounded-full border border-sand px-3.5 py-2">Annuler</button>
                  </div>
                </div>
              )}
              {c.blocked && (
                <button onClick={() => setBlocked(false, "")} disabled={saving} data-testid="client-unblock-btn"
                  className="inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-green text-white px-3.5 py-2 font-semibold disabled:opacity-40">
                  <ShieldCheck className="w-3.5 h-3.5" /> Débloquer ce client
                </button>
              )}
              {c.block_history.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="client-block-history">
                  <div className="text-[10px] uppercase tracking-widest text-mute font-bold">Historique blocages</div>
                  {[...c.block_history].reverse().map((h, i) => (
                    <div key={i} className="text-xs text-mute">
                      {h.action === "block" ? "🔒 Bloqué" : "🔓 Débloqué"} le {fmtDate(h.at)}{h.by_name ? ` par ${h.by_name}` : ""}{h.reason ? ` — « ${h.reason} »` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Historique des commandes */}
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-mute font-bold mb-2">Historique des commandes ({data.orders.length})</div>
              <div className="space-y-2" data-testid="client-orders-history">
                {data.orders.map((o) => {
                  const cancelReason = o.cancellation?.reason;
                  const byClient = o.status === "cancelled_in_delivery" || (o.status === "cancelled" && o.cancellation?.by_role === "client");
                  return (
                    <div key={o.order_id} className="rounded-xl border border-sand px-3 py-2 text-sm" data-testid={`client-order-${o.order_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono text-xs">#{o.order_id.slice(-6).toUpperCase()}</span>
                        <span className="text-xs text-mute">{o.restaurant_name}</span>
                        <span className="font-semibold">{formatGNF(o.total)}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: (STATUS_COLOR[o.status] || "#888") + "22", color: STATUS_COLOR[o.status] || "#888" }}>
                          {STATUS_LABEL[o.status] || o.status}
                        </span>
                        <span className="text-xs text-mute">{fmtDate(o.created_at)}</span>
                      </div>
                      {(cancelReason || o.refund_info) && (
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-mute">
                          {cancelReason && (
                            <span>
                              Motif {byClient ? "(client)" : `(${o.cancellation?.by_role || "?"})`} : {CANCEL_REASON_LABEL[cancelReason] || cancelReason}
                              {o.cancellation?.comment ? ` — « ${o.cancellation.comment} »` : ""}
                            </span>
                          )}
                          {o.refund_info && (
                            <span className={o.refund_info.status === "refunded" ? "text-brand-green" : "text-brand-red"}>
                              {o.refund_info.status === "refunded" ? `Remboursé ${formatGNF(o.refund_info.amount)}` : "Remboursement en échec"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {data.orders.length === 0 && <div className="text-sm text-mute">Aucune commande.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
